import { inboxClient } from "../../inbox/api/client";
import { instance as boompayClient } from "api/apiBase";
import type {
  Mailbox,
  UpdateMailboxRequest,
  SmsTemplate,
  SmsVariable,
  SmsTemplateListResponse,
  SmsTemplatePayload,
  PhoneNumber,
  PhoneNumbersListResponse,
  UpdatePhoneNumberRequest,
  RequestPhoneNumberRequest,
  SearchAvailableParams,
  AvailablePhoneNumber,
  PurchasePhoneNumberRequest,
  PropertyPhoneDefault
} from "./types";

export const mailboxesApi = {
  list: async () => {
    const { data } = await inboxClient.get<Mailbox[]>("/api/portal/mailboxes");
    return data;
  },

  get: async (id: string) => {
    const { data } = await inboxClient.get<Mailbox>(
      `/api/portal/mailboxes/${id}`
    );
    return data;
  },

  update: async (id: string, payload: UpdateMailboxRequest) => {
    const { data } = await boompayClient.patch<Mailbox>(
      `/portal/v1/communication_mailboxes/${id}`,
      payload
    );
    return data;
  },

  setDefault: async (id: string) => {
    await boompayClient.put(`/portal/v1/communication_mailboxes/${id}/default`);
  },

  removeDefault: async () => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/default`);
  },

  setPropertyDefault: async (propertyId: string, mailboxId: string) => {
    await boompayClient.put(
      `/portal/v1/communication_mailboxes/properties/${propertyId}/default`,
      { mailbox_id: mailboxId }
    );
  },

  removePropertyDefault: async (propertyId: string) => {
    await boompayClient.delete(
      `/portal/v1/communication_mailboxes/properties/${propertyId}/default`
    );
  },

  disconnect: async (id: string) => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/${id}`);
  },

  subscribe: async (id: string) => {
    await boompayClient.post(`/portal/v1/communication_mailboxes/${id}/sync`);
  },

  unsubscribe: async (id: string) => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/${id}/sync`);
  }
};

const SMS_TEMPLATES_BASE = "/portal/v1/boom_crm/sms_templates";

export const smsTemplatesApi = {
  list: async (page = 1, perPage = 100) => {
    const { data } = await boompayClient.get<SmsTemplateListResponse>(
      SMS_TEMPLATES_BASE,
      { params: { page, per_page: perPage } }
    );
    return data;
  },

  get: async (id: number) => {
    const { data } = await boompayClient.get<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}`
    );
    return data;
  },

  create: async (payload: SmsTemplatePayload) => {
    const { data } = await boompayClient.post<SmsTemplate>(
      SMS_TEMPLATES_BASE,
      payload
    );
    return data;
  },

  update: async (id: number, payload: Partial<SmsTemplatePayload>) => {
    const { data } = await boompayClient.patch<SmsTemplate>(
      `${SMS_TEMPLATES_BASE}/${id}`,
      payload
    );
    return data;
  },

  delete: async (id: number) => {
    await boompayClient.delete(`${SMS_TEMPLATES_BASE}/${id}`);
  },

  variables: async (subjectType = "lead") => {
    const { data } = await boompayClient.get<{ variables: SmsVariable[] }>(
      `${SMS_TEMPLATES_BASE}/variables`,
      { params: { subject_type: subjectType } }
    );
    return data.variables;
  }
};

const PHONE_BASE = "/api/portal/provisioned_phone_numbers";

export const phoneNumbersApi = {
  list: async () => {
    const { data } =
      await inboxClient.get<PhoneNumbersListResponse>(PHONE_BASE);
    return data;
  },

  get: async (id: string) => {
    const { data } = await inboxClient.get<PhoneNumber>(`${PHONE_BASE}/${id}`);
    return data;
  },

  searchAvailable: async (params: SearchAvailableParams) => {
    const { data } = await inboxClient.get<AvailablePhoneNumber[]>(
      `${PHONE_BASE}/available`,
      { params }
    );
    return data;
  },

  purchase: async (payload: PurchasePhoneNumberRequest) => {
    const { data } = await inboxClient.post<PhoneNumber>(
      `${PHONE_BASE}/purchase`,
      payload
    );
    return data;
  },

  request: async (payload?: RequestPhoneNumberRequest) => {
    const { data } = await inboxClient.post<PhoneNumber>(
      `${PHONE_BASE}/request`,
      payload
    );
    return data;
  },

  setDefault: async (id: string) => {
    const { data } = await inboxClient.put<PhoneNumber>(
      `${PHONE_BASE}/${id}/default`
    );
    return data;
  },

  update: async (id: string, payload: UpdatePhoneNumberRequest) => {
    const { data } = await inboxClient.patch<PhoneNumber>(
      `${PHONE_BASE}/${id}`,
      payload
    );
    return data;
  },

  remove: async (id: string) => {
    await inboxClient.delete(`${PHONE_BASE}/${id}`);
  },

  listPropertyDefaults: async () => {
    const { data } = await inboxClient.get<PropertyPhoneDefault[]>(
      `${PHONE_BASE}/properties/defaults`
    );
    return data;
  },

  getPropertyDefault: async (propertyId: number) => {
    const { data } = await inboxClient.get<PropertyPhoneDefault | null>(
      `${PHONE_BASE}/properties/${propertyId}/default`
    );
    return data;
  },

  setPropertyDefault: async (propertyId: number, phoneNumberId: string) => {
    const { data } = await inboxClient.put(
      `${PHONE_BASE}/properties/${propertyId}/default`,
      { phone_number_id: phoneNumberId }
    );
    return data;
  },

  removePropertyDefault: async (propertyId: number) => {
    await inboxClient.delete(`${PHONE_BASE}/properties/${propertyId}/default`);
  }
};
